import { Box, Button } from '@chakra-ui/react';
import { faFileExcel } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import ReactExport from 'react-export-excel';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import c from '../../../constant';
import useStore from '../../../store';
import getDepartmentLabel from '../../../utils/getDepartmentLabel';
import getPositionLabel from '../../../utils/getPositionLabel';

const ExcelFile = ReactExport.ExcelFile;
const ExcelSheet = ReactExport.ExcelFile.ExcelSheet;
const ExcelColumn = ReactExport.ExcelFile.ExcelColumn;

const toStr = (value) => String(value ?? '');

const PositionStatusRankLabel = {
	Level_7: 'អនុមន្រ្តីកម្រិត ១',
	Level_6: 'អនុមន្រ្តីកម្រិត ២',
	Level_5: 'អនុមន្រ្តីកម្រិត ៣',
	Level_4: 'នាយក្រមការ',
	Level_3: 'ក្រមការដើមខ្សែ',
	Level_2: 'ក្រមការ',
	Level_1: 'កម្មសិក្សា',
};

export default function ExportExcelPositionStatus({ records = [], ...props }) {
	const { t } = useTranslation();
	const { currentLang: lang } = useStore((state) => state.currentLang);

	const filename = lang === 'en' ? 'Position_Status' : 'ស្ថានភាពមុខងារបច្ចុប្បន្ន';

	function getOfficeLabel(currentOffice) {
		if (currentOffice) {
			switch (currentOffice) {
				case 'office1':
					return lang === 'kh' ? 'ការិយាល័យរដ្ឋបាល' : 'Administrative';
				case 'office2':
					return lang === 'kh' ? 'ការិយាល័យគណនេយ្យ' : 'Accounting';
				case 'office3':
					return lang === 'kh' ? 'ការិយាល័យធនធានមនុស្ស' : 'Human Resource';
				case 'office4':
					return lang === 'kh' ? 'ការិយាល័យទំនាក់ទនងសាធារណៈ' : 'Public Relation';
				case 'office5':
					return lang === 'kh' ? 'ការិយាល័យគ្រប់គ្រងអាជ្ញាបណ្ណ' : 'Licensing';
				case 'office6':
					return lang === 'kh' ? 'ការិយាល័យចុះបញ្ជីអន្តរការី' : 'Intermediary Registration';
				case 'office7':
					return lang === 'kh' ? 'ការិយាល័យនីតិកម្ម និងផ្សះផ្សាវិវាទ' : 'Legal and Mediation';
				case 'office8':
					return lang === 'kh' ? 'ការិយាល័យត្រួតពិនិត្យក្រុមហ៊ុនធានារ៉ាប់រងអាយុជីវិត' : 'Life Insurer';
				case 'office9':
					return lang === 'kh' ? 'ការិយាល័យត្រួតពិនិត្យក្រុមហ៊ុនធានារ៉ាប់រងទូទៅ' : 'General Insurer';
				case 'office10':
					return lang === 'kh' ? 'ការិយាល័យត្រួតពិនិត្យក្រុមហ៊ុនធានារ៉ាប់រងខ្នាតតូច​ និង​អន្តរការីធានារ៉ាប់រង' : 'Micro Insurer';
				case 'office11':
					return lang === 'kh' ? 'ការិយាល័យបទប្បញ្ញត្តិប្រុងប្រយ័ត្ន' : 'Prudential';
				case 'office12':
					return lang === 'kh' ? 'ការិយាល័យផលិតផលធានារ៉ាប់រងអាយុជីវិត' : 'Life Insurance Product';
				case 'office13':
					return lang === 'kh' ? 'ការិយាល័យផលិតផលធានារ៉ាប់រងទូទៅ' : 'General Insurance Product';
				case 'office14':
					return lang === 'kh' ? 'ការិយាល័យផលិតផលធានារ៉ាប់រងខ្នាតតូច' : 'Micro Insurance Product';
				case 'office15':
					return lang === 'kh' ? 'ការិយាល័យត្រួតពិនិត្យប្រតិបត្តិការទីផ្សារ' : 'Market Conduct Supervision';
				case 'office16':
					return lang === 'kh' ? 'ការិយាល័យស្រាវជ្រាវ' : 'Research';
				case 'office17':
					return lang === 'kh' ? 'ការិយាល័យបណ្តុះបណ្តាល' : 'Training';
				case 'office18':
					return lang === 'kh' ? 'ការិយាល័យសហប្រតិបត្តិការ' : 'Cooperation';
				case 'office19':
					return lang === 'kh' ? 'ការិយាល័យបច្ចេកវិទ្យាធានារ៉ាប់រង' : 'InsurTech';
				default:
					return '';
			}
		}
	}

	const data = useMemo(() => {
		return (
			records?.map((item) => ({
				no: item?.no ? String(item?.no).padStart(4, '0') : '',
				officials: toStr(lang === 'kh' ? item?.user?.fullNameKhmer : item?.user?.fullName),
				department: item?.department === 'N/A' ? 'N/A' : item?.department ? toStr(t(getDepartmentLabel(item?.department))) : 'N/A',
				office: item?.office === 'N/A' ? 'N/A' : item?.office ? toStr(t(getOfficeLabel(item?.office))) : 'N/A',
				position: item?.position ? toStr(t(lang === 'kh' ? getPositionLabel(item?.position) : getPositionLabel(item?.position, 'en'))) : 'N/A',
				rank: item?.rank ? PositionStatusRankLabel[item?.rank] || item?.rank : 'N/A',
				organisation: item?.organisation ? toStr(lang === 'en' ? 'Insurance Regulator of Cambodia' : 'និយ័តករធានារ៉ាប់រងកម្ពុជា') : 'N/A',
				dateOfLastRank: item?.dateOfLastRank ? moment(item?.dateOfLastRank).format('YYYY-MM-DD') : '',
				dateOfLastPosition: item?.dateOfLastPosition ? moment(item?.dateOfLastPosition).format('YYYY-MM-DD') : '',
				createdAt: item?.createdAt ? moment(item?.createdAt).format('YYYY-MM-DD HH:mm') : '',
			})) || []
		);
	}, [records, lang, t]);

	return (
		<Box {...props}>
			<ExcelFile
				filename={
					lang === 'en'
						? `${filename}_Generated_At_${moment().format(c.excelDateTimeFormat)}`
						: `${filename}_បង្កើតនៅថ្ងៃ_${moment().format(c.excelDateTimeFormat)}`
				}
				element={
					<Button
						type="button"
						leftIcon={<FontAwesomeIcon icon={faFileExcel} style={{ fontSize: 16 }} />}
						colorScheme="gray"
						variant="solid"
						size="sm"
						borderRadius={c.borderRadius}
						border="1px solid #bdc3c7"
						isDisabled={!records?.length}
					>
						{t('Export')}
					</Button>
				}
			>
				<ExcelSheet data={data} name="Position Status">
					<ExcelColumn label={t('Number')} value="no" />
					<ExcelColumn label={t('Officials')} value="officials" />
					<ExcelColumn label={t('Department')} value="department" />
					<ExcelColumn label={t('Office')} value="office" />
					<ExcelColumn label={t('Position')} value="position" />
					<ExcelColumn label={t('Date of Last Position')} value="dateOfLastPosition" />
					<ExcelColumn label={t('Rank')} value="rank" />
					<ExcelColumn label={t('Date of Last Rank')} value="dateOfLastRank" />
					<ExcelColumn label={t('Organisation')} value="organisation" />
					<ExcelColumn label={t('Created At')} value="createdAt" />
				</ExcelSheet>
			</ExcelFile>
		</Box>
	);
}
